import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { rolePermissionService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  ShieldCheckIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { Spinner } from '../components/Spinner';

const MODULES = [
  { key: 'leads',                label: 'Leads',                actions: ['create', 'read', 'update', 'delete', 'bulkUpload'] },
  { key: 'followups',            label: 'Follow-ups',           actions: ['create', 'read', 'update', 'delete'] },
  { key: 'conversions',          label: 'Conversions',          actions: ['create', 'read', 'update', 'delete'] },
  { key: 'reports',              label: 'Reports',              actions: ['view'] },
  { key: 'dashboard',            label: 'Dashboard',            actions: ['view'] },
  { key: 'users',                label: 'Users',                actions: ['create', 'read', 'update', 'delete', 'bulkUpload'] },
  { key: 'brands',               label: 'Brands & Models',      actions: ['view'] },
  { key: 'facebook_integration', label: 'Facebook Integration', actions: ['view'] },
  { key: 'whatsapp_integration', label: 'WhatsApp Integration', actions: ['view'] },
  { key: 'google_integration',   label: 'Google Ads',           actions: ['view'] },
  { key: 'website_integration',  label: 'Website Integration',  actions: ['view'] },
];

const ALL_ACTIONS = ['create', 'read', 'update', 'delete', 'view', 'bulkUpload'];
const ACTION_LABELS = { bulkUpload: 'Bulk Upload' };

const ROLE_LABELS = {
  super_admin:     { label: 'Super Admin',     color: 'badge-danger',    editable: false },
  admin:           { label: 'Admin',           color: 'badge-warning',   editable: true  },
  dealer:          { label: 'Dealer',          color: 'badge-primary',   editable: true  },
  sales_executive: { label: 'Sales Executive', color: 'badge-secondary', editable: true  },
};

// A user only sees the roles below their own (the backend returns the same
// set); super admins also see their own, fixed, row.
const VISIBLE_ROLES = {
  super_admin: ['super_admin', 'admin', 'dealer', 'sales_executive'],
  admin:       ['dealer', 'sales_executive'],
};

export const RolePermissions = () => {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery('rolePermissions', rolePermissionService.getRolePermissions);

  const [editing, setEditing] = useState(null);       // roleName being edited
  const [draft, setDraft]     = useState(null);       // draft permissions object

  const updateMutation = useMutation(
    ({ role, permissions }) => rolePermissionService.updateRolePermissions(role, permissions),
    {
      onSuccess: (_, { role }) => {
        toast.success(`Permissions for ${ROLE_LABELS[role]?.label} updated`);
        queryClient.invalidateQueries('rolePermissions');
        setEditing(null);
        setDraft(null);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || 'Failed to update permissions');
      },
    }
  );

  if (currentUser.role !== 'super_admin' && currentUser.role !== 'admin') {
    return (
      <div className="text-center py-12">
        <ShieldCheckIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h2 className="mt-2 text-lg font-medium text-gray-900">Access Denied</h2>
        <p className="mt-1 text-gray-500">Only super admins and admins can manage role permissions.</p>
      </div>
    );
  }

  const rolePermissions = data?.data?.data?.rolePermissions || data?.data?.rolePermissions || {};

  const startEdit = (roleName) => {
    // Deep clone current permissions as draft
    setDraft(JSON.parse(JSON.stringify(rolePermissions[roleName] || {})));
    setEditing(roleName);
  };

  const cancelEdit = () => { setEditing(null); setDraft(null); };

  const toggleDraft = (module, action) => {
    setDraft(prev => ({
      ...prev,
      [module]: { ...prev[module], [action]: !prev[module]?.[action] },
    }));
  };

  const handleSave = () => {
    updateMutation.mutate({ role: editing, permissions: draft });
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Role Permissions</h1>
        <p className="mt-2 text-gray-600">
          Manage default permissions for the roles below yours. Changes apply to every user of that role.
        </p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : (
        <div className="space-y-6">
          {(VISIBLE_ROLES[currentUser.role] || []).map(roleName => {
            const { label, color, editable } = ROLE_LABELS[roleName];
            const perms = editing === roleName ? draft : rolePermissions[roleName];
            const isEditing = editing === roleName;

            return (
              <div key={roleName} className="bg-white shadow rounded-lg overflow-hidden">
                {/* Role header */}
                <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <ShieldCheckIcon className="h-5 w-5 text-gray-400" />
                    <span className="text-base font-semibold text-gray-900">{label}</span>
                    <span className={`badge ${color}`}>{label}</span>
                    {!editable && (
                      <span className="text-xs text-gray-400 italic">Full access — not editable</span>
                    )}
                  </div>
                  {editable && !isEditing && (
                    <button
                      onClick={() => startEdit(roleName)}
                      className="btn-outline-primary text-sm"
                    >
                      Edit Permissions
                    </button>
                  )}
                  {isEditing && (
                    <div className="flex gap-2">
                      <button
                        onClick={cancelEdit}
                        className="btn-outline-secondary text-sm"
                        disabled={updateMutation.isLoading}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSave}
                        className="btn-primary text-sm"
                        disabled={updateMutation.isLoading}
                      >
                        {updateMutation.isLoading
                          ? <><Spinner size="sm" className="mr-1" />Saving...</>
                          : 'Save Changes'}
                      </button>
                    </div>
                  )}
                </div>

                {/* Permissions table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-left px-6 py-3 font-medium text-gray-600 w-40">Module</th>
                        {ALL_ACTIONS.map(a => (
                          <th key={a} className="text-center px-4 py-3 font-medium text-gray-600 capitalize w-24">{ACTION_LABELS[a] || a}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {MODULES.map(({ key, label: modLabel, actions }) => (
                        <tr key={key} className="hover:bg-gray-50">
                          <td className="px-6 py-3 font-medium text-gray-800">{modLabel}</td>
                          {ALL_ACTIONS.map(action => (
                            <td key={action} className="px-4 py-3 text-center">
                              {actions.includes(action) ? (
                                isEditing ? (
                                  <input
                                    type="checkbox"
                                    checked={draft?.[key]?.[action] || false}
                                    onChange={() => toggleDraft(key, action)}
                                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
                                  />
                                ) : (
                                  perms?.[key]?.[action]
                                    ? <CheckCircleIcon className="h-5 w-5 text-green-500 mx-auto" />
                                    : <XCircleIcon className="h-5 w-5 text-gray-300 mx-auto" />
                                )
                              ) : (
                                <span className="text-gray-200">—</span>
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
